import { call, put, select, all } from 'redux-saga/effects';
import * as actions from './actions';
import { Base64 } from 'js-base64';
import axios from 'axios';
import { waitFor } from 'store/helpers';
import { sagas as eventSagas } from 'store/event';
import { AUTO_SAVE_TIMER_MS } from 'constants.js';
import { updateSurveyTemplateFilters } from './actions';
import { getParams, safeBase64Encode, safeBase64Decode } from 'store/helpers/helperFunctions';

const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;

export function* waitForGetSurveyTemplates() {
  return yield waitFor(
    actions.getSurveyTemplates(),
    actions.GETTING_SURVEY_TEMPLATES_SUCCEEDED,
    actions.GETTING_SURVEY_TEMPLATES_FAILED
  );
}

// hydration and shape helpers
const decodeTemplateContent = template => {
  if (template.type === 'EMAIL') {
    template.jsonContent = safeBase64Decode(template.jsonContent);
    template.content = safeBase64Decode(template.content);
  } else {
    template.content = safeBase64Decode(template.content);
  }
  return template;
};

const encodeTemplateContent = template => {
  const encodedTemplate = { ...template };

  if (encodedTemplate.type === 'EMAIL') {
    if (encodedTemplate.content) {
      encodedTemplate.content = safeBase64Encode(encodedTemplate.content);
    } else {
      encodedTemplate.content = safeBase64Encode(encodedTemplate.jsonContent.html);
    }
    encodedTemplate.jsonContent = safeBase64Encode(encodedTemplate.jsonContent);
  } else {
    encodedTemplate.content = safeBase64Encode(encodedTemplate.content);
  }

  return encodedTemplate;
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;

const getSurveyTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/surveyResultResponse-template`;

const getTagsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/tags/surveyResultResponse-template`;

let isArchiveInProgress = false;

export const getSurveyTemplatesAsync = (
  tenantId,
  businessContextId,
  templateUuid
) =>
  axios
    .get(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}${templateUuid ? `/uuid/${templateUuid}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const getSurveyTemplatesWithIdAsync = (
  tenantId,
  businessContextId,
  templateId
) =>
  axios
    .get(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}${
        templateId ? `/${templateId}` : ''
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedSurveyTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getMinimalSurveyTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getTagsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getTagsApiRoot(tenantId, businessContextId)}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createSurveyTemplateAsync = (
  tenantId,
  businessContextId,
  template
) =>
  axios
    .post(`${getSurveyTemplateApiRoot(tenantId, businessContextId)}`, template)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const testSurveyTemplateAsync = (
  tenantId,
  businessContextId,
  testTemplateData
) =>
  axios
    .post(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/verify`,
      testTemplateData
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateSurveyTemplateAsync = (
  tenantId,
  businessContextId,
  template
) =>
  axios
    .put(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/uuid/${template.uuid}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const updateSurveyTemplateWithIdAsync = (
  tenantId,
  businessContextId,
  template
) =>
  axios
    .put(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/${template.id}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteSurveyTemplateAsync = (
  tenantId,
  businessContextId,
  templateUuid
) =>
  axios
    .delete(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/uuid/${templateUuid}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

//deprecated
export const deleteSurveyTemplateWithIdAsync = (
  tenantId,
  businessContextId,
  templateId
) =>
  axios
    .delete(
      `${getSurveyTemplateApiRoot(tenantId, businessContextId)}/${templateId}`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

// sagas

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentTemplate = store =>
  store.surveyTemplate ? store.surveyTemplate.current : {};
const templateList = store =>
  store.surveyTemplate ? store.surveyTemplate.list : [];
const paginatedTemplateList = store =>
  store.surveyTemplate ? store.surveyTemplate.paginatedList : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const tags = store =>
  store.surveyTemplate ? store.surveyTemplate.tagList : [];
const searchInput = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.searchInput : '';
const displayActive = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.displayActive : true;
const sortBy = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.sortBy : 'name';
const sortOrder = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.pageNo : 0;
const totalCount = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.totalCount : 0;
const filterTemplates = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.filterProductCategories : [];
const filterTags = store =>
  store.surveyTemplate.filters ? store.surveyTemplate.filters?.filterTags : [];
// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

export function* getSurveyTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(templateList);
    if (
      action.type === actions.GET_SURVEY_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      yield call(waitForGetSupportedEventTypes);
      yield put(actions.gettingSurveyTemplates());
      currentList = yield call(
        getSurveyTemplatesAsync,
        tenantId,
        businessContextId
      );
      currentList = currentList.map(decodeTemplateContent);
    }

    yield put(actions.gettingSurveyTemplatesSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingSurveyTemplatesFailed(ex));
  }
}

export function* getPaginatedSurveyTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedTemplateList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_SURVEY_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        searchScopes: yield select(filterTemplates),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        tags: yield select(filterTags)
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updateSurveyTemplateFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedSurveyTemplates());
      currentList = yield call(
        getPaginatedSurveyTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(updateSurveyTemplateFilters({ totalCount: currentList.totalRecords }));
      currentList = currentList.data.map(decodeTemplateContent);
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedSurveyTemplatesSucceeded(currentList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updateSurveyTemplateFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedSurveyTemplatesFailed(ex));
  }
}

export function* getMinimalSurveyTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    
    if (action.type === actions.GET_MINIMAL_SURVEY_TEMPLATES) {
      yield put(actions.gettingMinimalSurveyTemplates());
      
      let urlParams = {
        index: payload.index || 0,
        pageSize: payload.pageSize || 100
      };
      
      let bodyParams = {
        active: payload.active !== undefined ? payload.active : true,
        ...(payload.productCategory && { productCategory: payload.productCategory }),
        ...(payload.productName && { productName: payload.productName }),
        ...(payload.eventTypeCategory && { eventTypeCategory: payload.eventTypeCategory }),
        ...(payload.name && { name: payload.name })
      };
      
      let currentList = yield call(
        getMinimalSurveyTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      
      yield put(actions.gettingMinimalSurveyTemplatesSucceeded({
        data: currentList.data,
        totalRecords: currentList.totalRecords
      }));
    }
  } catch (ex) {
    yield put(actions.gettingMinimalSurveyTemplatesFailed(ex));
  }
}

export function* getTagsIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let tagList = yield select(tags);

    if (action.type === actions.GET_SURVEY_TEMPLATE_TAGS) {
      yield put(actions.gettingSurveyTemplateTags());
      tagList = yield call(getTagsAsync, tenantId, businessContextId);
    }
    yield put(actions.gettingSurveyTemplateTagsSucceeded(tagList));
  } catch (ex) {
    yield put(actions.gettingSurveyTemplateTagsFailed(ex));
  }
}

export function* getSurveyTemplateIfNeeded(action) {
  const { payload } = action;
  try {
    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes)
    };

    yield put(actions.gettingSurveyTemplate());

    let tenantId = yield select(currentTenantId);
    let businessContextId = payload.businessContextId || (yield select(currentBusinessContextId));
    let template = yield select(currentTemplate);
    let templateUuid = payload.templateUuid;

    if ((templateUuid && template.uuid !== templateUuid) || payload.force === true) {
      template = yield call(
        getSurveyTemplatesAsync,
        tenantId,
        businessContextId,
        templateUuid
      );

      template = decodeTemplateContent(template);
    }

    let eventMetaData, metaDataPath;
    if (template.schemaIdentifier) {
      ({ eventMetaData, metaDataPath } = yield call(waitForGetEventMetaData, {
        schemaIdentifier: template.schemaIdentifier,
        productName: template.productName,
        workflow: 'template',
        productType: template.productType
      }));
    }

    if (!template.hasOwnProperty('productCategory')) {
      template = { ...template, productCategory: metaDataPath?.[0] };
    }

    yield put(
      actions.gettingSurveyTemplateSucceeded({ template, eventMetaData })
    );
  } catch (ex) {
    yield put(actions.gettingSurveyTemplateFailed(ex));
  }
}

export function* createSurveyTemplateFromCurrent() {
  try {
    yield put(actions.creatingSurveyTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;

    const isCopyFlow = !!(template._isCopy || template._isCopyTemplate);
    if (isCopyFlow) {
      template = { ...template };
      delete template.id;
      delete template.uuid;
    }

    if (!template.uuid) {
      template = encodeTemplateContent({ ...template });

      template = yield call(
        createSurveyTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }

    template = decodeTemplateContent(template);
    template = { ...template, eventMetaData: eventMetaData };

    yield put(actions.creatingSurveyTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.creatingSurveyTemplateFailed(ex));
  }
}

export function* updateSurveyTemplateFromCurrent() {
  try {
    yield put(actions.updatingSurveyTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;
    if (template.uuid) {
      template = encodeTemplateContent({ ...template });

      template = yield call(
        updateSurveyTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }

    template = decodeTemplateContent(template);
    template = { ...template, eventMetaData: eventMetaData };

    yield put(actions.updatingSurveyTemplateSucceeded(template));
  } catch (ex) {
    yield put(actions.updatingSurveyTemplateFailed(ex));
  }
}

export function* deleteSurveyTemplatesByIdList(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let templateUuids = payload.templateUuids;
    let templates = yield select(paginatedTemplateList);

    let deletedTemplateStatuses = [];
    if (templateUuids.length > 0) {
      yield put(actions.deletingSurveyTemplates());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete template API method can be made to return the deleted id this could be set to a variable and report partial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => [
              deleteSurveyTemplateAsync,
              tenantId,
              businessContextId,
              templateUuid
            ])
            .map(reflect)
        );
      } else {
        deletedTemplateStatuses = yield all(
          templateUuids
            .map(templateUuid => {
              const template = templates.find(t => t.uuid === templateUuid);
              const encodedTemplate = encodeTemplateContent({ ...template });
              return [
                updateSurveyTemplateAsync,
                tenantId,
                businessContextId,
                {
                  ...encodedTemplate,
                  active: !payload.activeStatus
                }
              ];
            })
            .map(reflect)
        );
      }
    }
    if (
      deletedTemplateStatuses.filter(status => status.status === 'resolved')
        .length === templateUuids.length
    ) {
      isArchiveInProgress = true;
      yield all([
        put(
          actions.deletingSurveyTemplatesSucceeded(
            templateUuids,
            payload.hardDelete
          )
        ),
        put(actions.getPaginatedSurveyTemplates({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingSurveyTemplatesFailed({
          message: 'Not all requested templates could be deleted.',
          deletedTemplateStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingSurveyTemplatesFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}

export function* testSurveyTemplate() {
  try {
    yield put(actions.testingSurveyTemplate());
    let template = yield select(currentTemplate);
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);

    template = encodeTemplateContent({ ...template });

    let testedResponse = yield call(
      testSurveyTemplateAsync,
      tenantId,
      businessContextId,
      template
    );

    yield put(actions.testingSurveyTemplateSucceeded(testedResponse));
  } catch (ex) {
    yield put(actions.testingSurveyTemplateFailed(ex));
  }
}

let timer;
export function* autoSaveTimerStart() {
  if (timer) {
    clearTimeout(timer);
  }

  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, AUTO_SAVE_TIMER_MS);
      })
  );

  yield put(actions.templateAutoSaveTimerEnd());
}
